import { Organization, Person, User, UserModel } from '../../gs-core/common';
import '../../gs-core/common/util/array.prototype';

import { SubjectType, SubjectTypes } from './SubjectType';
import { ViewAccess, ViewAccessTypes } from './ViewAccess';

export interface Subject {
  user: User | Person;
  type: SubjectType;
  groups: Organization[];
  getIdentifier: () => string | number | undefined;
  getRoute: () => string;
  toJSON: () => Partial<Subject>;
}

export const SubjectModel = {
  create(props: Partial<Subject>, viewAccess: ViewAccess = ViewAccessTypes.SUBJECTS_GROUPS): Subject {
    const user = UserModel.create(props.user as User);

    if (!user) {
      throw new Error('Missing subject user');
    }

    const type = SubjectTypes.parse(props.type as SubjectType);
    const roleForThisType = SubjectTypes.toRole(type);

    const groupOverrides = (props.groups || [])
      .map(group => UserModel.create(group));

    let usersGroups: Organization[] = [];

    const possibleSubjectRoles = (user.roles || [])
      .filter(userRole => userRole.role.code === roleForThisType.code);

    // Program subjects are their own group, GRADUATE_SCHOOL group would otherwise allow global access
    if (type === SubjectTypes.PROGRAM) {
      if (viewAccess !== ViewAccessTypes.OWNING_UNIT && viewAccess !== ViewAccessTypes.SUBJECTS_GROUPS) {
        throw new Error(`Cannot make a ${type} subject with viewAccess ${viewAccess}`);
      }

      if (possibleSubjectRoles.length) {
        groupOverrides.push(user);
      }
    } else {
      const topOrganizations = SubjectTypes.topOrganizations(type);

      usersGroups = possibleSubjectRoles
        .filter(userRole =>
          userRole.role.code === roleForThisType.code &&
          userRole.group.isUnderAny &&
          userRole.group.isUnderAny(topOrganizations)
        )
        .map(userRole => UserModel.create(userRole.group) as Organization);
    }

    // Put groupOverrides first since unique will keep the first occurrence
    const groups = [...groupOverrides, ...usersGroups]
      .filter(group => !!group)
      .unique() as Organization[];

    if (!groups.length) {
      throw new Error('Missing subject groups');
    }

    return { user, type, groups, getIdentifier, getRoute, toJSON };
  },

  createMultipleFrom(user: User): Subject[] {
    return SubjectTypes.all()
      .map(type => {
        try {
          return SubjectModel.create({ user, type });
        } catch (err) {
          if (err.message !== 'Missing subject groups') {
            throw err;
          }
          return null;
        }
      })
      .filter(subject => !!subject) as Subject[];
  }
};

function getIdentifier(this: Subject): string | number | undefined {
  let identifier: string | number | undefined;

  switch (this.type) {
    case SubjectTypes.STUDENT:
      identifier = (this.user as Person).studentId;
      break;
    case SubjectTypes.POSTDOC:
      identifier = (this.user as Person).employeeId;
      break;
    case SubjectTypes.PROGRAM:
      identifier = (this.user as Organization).code;
      break;
    default:
      throw new Error('Invalid type');
  }

  return identifier;
}

function getRoute(this: Subject): string {
  return `/${this.type.toLowerCase()}/${this.user.id}`;
}

function toJSON(this: Subject): Partial<Subject> {
  // Only save the bare minimum user / group data to reduce data size and decouple from PRM
  return {
    user: {
      id: this.user.id,
      code: this.user.code,
      name: this.user.name,
      clazz: this.user.clazz
    },
    type: this.type,
    groups: this.groups.map(group => ({
      id: group.id,
      code: group.code,
      name: group.name,
      clazz: group.clazz
    }))
  };
}



// WEBPACK FOOTER //
// ./src/common/models/Subject.ts